// https://react-query.tanstack.com/examples/default-query-function
// This will allow us to define the root fetch to be used throughout the app

import { FetchMethodTypes, FetchMethods } from '@alluradirect/react';
import { generateBodyData, generateFetchOptions, generateURL, isSendMethod } from './helpers';
import * as Sentry from '@sentry/nextjs';

import { httpsUrl } from 'config/url';
import { NETWORK_ERROR_MESSAGE, reactQueryAPIErrorMessage, UNDEFINED_NETWORK_RESPONSE } from 'helpers/toastHelpers';
import { requestTracking } from '@alluradirect/react';

interface Options {
  values?: any;
  method?: FetchMethods | FetchMethodTypes;
  // Show/Hide toasts. Error toasts will always show
  isSubform?: boolean;
  // Ignore any potential data-transformations, data is already formatted
  isCustomSendData?: boolean;
  // The SUCCESS will be false. In this case, a manual quote needs to be created if SUCCESS = false
  shouldAcceptFalseSuccess?: boolean;
  /** By default deserializes data to json, provide a custom deserializer if needed */
  deserialize?: (res: Response) => Promise<any>;
}

// Utilize this function for mutations GET/PUT/POST/DELETE
// For use with React-Query
//@ts-ignore
export async function customQuery<T = unknown>(url: string, options: Options = {}): Promise<T> {
  // Give default values to options if no values were provided
  const {
    method = 'GET',
    values = null,
    isSubform = false,
    isCustomSendData = false,
    shouldAcceptFalseSuccess = false,
    deserialize = (res: Response) => res.json(),
  } = options;

  const endpoint = `${httpsUrl()}${url}`;
  const fetchURL = generateURL({ endpoint });
  // Flag to avoid duplicate toast sends within the same request

  requestTracking.push({ url: endpoint, method });

  const body = generateBodyData({
    isCustomSendData,
    isSubform,
    values,
    isSendMethod: isSendMethod(method),
  });

  const fetchOptions = generateFetchOptions({ isCustomSendData });

  const response = await fetch(fetchURL, {
    method: method,
    body,
    ...fetchOptions,
  }).catch(e => {
    Sentry.captureException(e);
    throw new Error(NETWORK_ERROR_MESSAGE);
  });

  if (!response) {
    const error = new Error(UNDEFINED_NETWORK_RESPONSE);
    Sentry.captureException(error);
    throw error;
  }

  //  response.status > 299 or response.status < 200
  if (!response.ok && !shouldAcceptFalseSuccess) {
    // Try to parse response data to get error message
    let error = undefined;
    try {
      const data = await deserialize(response);
      error = new Error(JSON.stringify(data));
      (error as any).response = response;
    } catch (e) {
      error = new Error(reactQueryAPIErrorMessage);
      (error as any).response = response;
    }
    Sentry.captureException(error);
    // handleClientErrorResponse(response.status);
    throw error;
  }

  requestTracking.update(endpoint, { responseCode: response.status });

  const data = await deserialize(response).catch((e: any) => {
    Sentry.captureException(e);
    throw new Error(reactQueryAPIErrorMessage);
  });

  // We should get rid of this edge case by removing shouldAcceptFalseSuccess
  // Once tapechart is ported we can remove this. It is currently used for booking quotes
  if (!shouldAcceptFalseSuccess && !data.SUCCESS) {
    Sentry.captureException(`data.SUCCESS is false and shouldAcceptFalseSuccess is false: ${JSON.stringify(data)}`);
    throw new Error(reactQueryAPIErrorMessage);
  }

  requestTracking.update(endpoint, { dataReceived: data });

  return data;
}
